/**
 * Notification Service Layer.
 *
 * Single ingress point for all notification-related interactions in the UI.
 * Components MUST go through this module — never call supabase notification
 * tables directly.
 *
 * - Reads come from the `notification_inbox_view` (RLS-scoped per user)
 *   and the `get_unread_notification_count` RPC.
 * - All mutations go through the `notifications-write` edge function.
 * - Direct .insert / .update / .delete / .upsert on notification tables is
 *   forbidden in the frontend (enforced by architecture guardrails).
 */
import { supabase } from "@/integrations/supabase/client";

export type NotificationSeverity = "info" | "success" | "warning" | "error";
export type NotificationStatus = "unread" | "read" | "dismissed" | "archived";
export type NotificationChannel = "in_app" | "email" | "push" | "digest";

export interface NotificationInboxItem {
  inbox_id: string;
  notification_id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  severity: NotificationSeverity;
  source_domain: string;
  source_type: string | null;
  source_id: string | null;
  action_url: string | null;
  metadata_json: Record<string, unknown>;
  status: NotificationStatus;
  channel: NotificationChannel;
  created_at: string;
  delivered_at: string | null;
  read_at: string | null;
  dismissed_at: string | null;
}

export interface NotificationPreference {
  id: string;
  user_id: string;
  notification_type: string;
  in_app_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  digest_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetNotificationsParams {
  /** Inbox status filter. `"all"` returns every status incl. dismissed/archived. */
  status?: NotificationStatus | "all";
  /** Notification type filter (exact match). */
  type?: string;
  /** Page size. Defaults to 50, capped at 200. */
  limit?: number;
}

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

function normalizeRow(row: Record<string, unknown>): NotificationInboxItem {
  return {
    inbox_id: String(row.inbox_id ?? ""),
    notification_id: String(row.notification_id ?? ""),
    user_id: String(row.user_id ?? ""),
    type: String(row.type ?? ""),
    title: String(row.title ?? ""),
    body: (row.body as string | null) ?? null,
    severity: ((row.severity as NotificationSeverity) ?? "info"),
    source_domain: String(row.source_domain ?? ""),
    source_type: (row.source_type as string | null) ?? null,
    source_id: (row.source_id as string | null) ?? null,
    action_url: (row.action_url as string | null) ?? null,
    metadata_json:
      (row.metadata_json && typeof row.metadata_json === "object"
        ? (row.metadata_json as Record<string, unknown>)
        : {}),
    status: ((row.status as NotificationStatus) ?? "unread"),
    channel: ((row.channel as NotificationChannel) ?? "in_app"),
    created_at: String(row.created_at ?? ""),
    delivered_at: (row.delivered_at as string | null) ?? null,
    read_at: (row.read_at as string | null) ?? null,
    dismissed_at: (row.dismissed_at as string | null) ?? null,
  };
}

/**
 * Read notifications from the inbox view.
 *
 * RLS guarantees the caller only sees their own inbox rows. By default,
 * `dismissed` and `archived` items are hidden — pass `status: "all"` (or a
 * specific status) to opt in.
 */
export async function getNotifications(
  params: GetNotificationsParams = {},
): Promise<NotificationInboxItem[]> {
  const limit = Math.min(Math.max(params.limit ?? DEFAULT_LIMIT, 1), MAX_LIMIT);

  // Admins can see all inbox rows via the security_invoker view (RLS allows
  // admin reads), but the inbox UI must only show the caller's own items.
  // Without this filter, mark_read/dismiss/archive on a foreign row would
  // be (correctly) rejected by the edge function with 403.
  const { data: authData } = await supabase.auth.getUser();
  const uid = authData?.user?.id;
  if (!uid) return [];

  let query = supabase
    .from("notification_inbox_view")
    .select("*")
    .eq("user_id", uid)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (params.status && params.status !== "all") {
    query = query.eq("status", params.status);
  } else if (!params.status) {
    // Default: hide dismissed + archived from inbox lists.
    query = query.in("status", ["unread", "read"]);
  }

  if (params.type) {
    query = query.eq("type", params.type);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((r) => normalizeRow(r as Record<string, unknown>));
}

/** Fetch the unread in-app count via secure RPC (no client-side counting). */
export async function getUnreadNotificationCount(): Promise<number> {
  const { data, error } = await supabase.rpc("get_unread_notification_count");
  if (error) throw error;
  const n = typeof data === "number" ? data : Number(data ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Read the current user's notification preferences.
 *
 * RLS scopes results to the authenticated user. Reads only — all writes
 * must go through the `notifications-write` edge function (see
 * `updateNotificationPreferences`).
 */
export async function getNotificationPreferences(): Promise<NotificationPreference[]> {
  const { data, error } = await supabase
    .from("notification_preferences")
    .select(
      "id, user_id, notification_type, in_app_enabled, email_enabled, push_enabled, digest_enabled, created_at, updated_at",
    )
    .order("notification_type", { ascending: true });
  if (error) throw error;
  return (data ?? []) as NotificationPreference[];
}

/* ── Mutations: all routed through `notifications-write` ─────────────── */

type WriteAction =
  | { action: "mark_read"; payload: { inbox_id: string } }
  | { action: "mark_all_read"; payload: { type?: string } }
  | { action: "dismiss"; payload: { inbox_id: string } }
  | { action: "dismiss_all"; payload: { type?: string } }
  | { action: "archive"; payload: { inbox_id: string } }
  | {
      action: "update_preferences";
      payload: {
        notification_type: string;
        in_app_enabled?: boolean;
        email_enabled?: boolean;
        push_enabled?: boolean;
        digest_enabled?: boolean;
      };
    }
  | {
      action: "admin_create_notification";
      payload: AdminCreateNotificationPayload;
    }
  | {
      action: "admin_schedule_notification";
      payload: AdminScheduleNotificationPayload;
    }
  | {
      action: "admin_cancel_scheduled_notification";
      payload: { job_id: string };
    }
  | {
      action: "admin_list_notification_templates";
      payload: { include_inactive?: boolean };
    }
  | {
      action: "admin_upsert_notification_template";
      payload: AdminUpsertNotificationTemplatePayload;
    }
  | {
      action: "admin_archive_notification_template";
      payload: { id: string };
    }
  | {
      action: "admin_run_email_test";
      payload: { title?: string; body?: string };
    }
  | {
      action: "admin_retry_delivery";
      payload: { delivery_id: string };
    }
  | {
      action: "admin_cancel_delivery";
      payload: { delivery_id: string; reason?: string };
    };

export type AdminAudienceMode =
  | "self"
  | "admins"
  | "all_users"
  | "specific_users";

export interface AdminCreateNotificationPayload {
  type?: string;
  title: string;
  body?: string | null;
  severity: NotificationSeverity;
  audience:
    | { mode: "self" }
    | { mode: "admins" }
    | { mode: "all_users" }
    | { mode: "specific_users"; user_ids: string[] };
  action_url?: string | null;
  metadata_json?: Record<string, unknown>;
}

export interface AdminCreateNotificationResult {
  notification_id: string;
  recipient_count: number;
}

export type AdminNotificationSendMode = "now" | "scheduled";

export interface AdminScheduleNotificationPayload
  extends AdminCreateNotificationPayload {
  /** ISO 8601 string. Must be at least +2 minutes and at most +180 days. */
  scheduled_for: string;
}

export interface AdminScheduleNotificationResult {
  ok: boolean;
  job_id: string | null;
  scheduled_for: string;
}

/* ── Notification Templates ─────────────────────────────────────────── */

export interface NotificationTemplate {
  id: string;
  key: string;
  label: string;
  description: string | null;
  type: string;
  severity: NotificationSeverity;
  title_template: string;
  body_template: string | null;
  action_url_template: string | null;
  metadata_json: Record<string, unknown>;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface AdminUpsertNotificationTemplatePayload {
  id?: string;
  key: string;
  label: string;
  description?: string | null;
  type: string;
  severity: NotificationSeverity;
  title_template: string;
  body_template?: string | null;
  action_url_template?: string | null;
  metadata_json?: Record<string, unknown>;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * All mutations use the nested `{ action, payload }` envelope expected by the
 * `notifications-write` edge function. Do not flatten — the function validates
 * with a strict Zod discriminated union and will 400 on a flat body.
 */
async function invokeWrite<T = unknown>(body: WriteAction): Promise<T> {
  const { data, error } = await supabase.functions.invoke("notifications-write", {
    body,
  });
  if (error) throw error;
  return data as T;
}

export async function markNotificationRead(inboxId: string): Promise<void> {
  await invokeWrite({ action: "mark_read", payload: { inbox_id: inboxId } });
}

export async function markAllNotificationsRead(
  type?: string,
): Promise<{ updated_count: number }> {
  const result = await invokeWrite<{ updated_count?: number }>({
    action: "mark_all_read",
    payload: type ? { type } : {},
  });
  return { updated_count: Number(result?.updated_count ?? 0) };
}

export async function dismissNotification(inboxId: string): Promise<void> {
  await invokeWrite({ action: "dismiss", payload: { inbox_id: inboxId } });
}

export async function dismissAllNotifications(
  type?: string,
): Promise<{ updated_count: number }> {
  const result = await invokeWrite<{ updated_count?: number }>({
    action: "dismiss_all",
    payload: type ? { type } : {},
  });
  return { updated_count: Number(result?.updated_count ?? 0) };
}

export async function archiveNotification(inboxId: string): Promise<void> {
  await invokeWrite({ action: "archive", payload: { inbox_id: inboxId } });
}

export async function updateNotificationPreferences(payload: {
  notification_type: string;
  in_app_enabled?: boolean;
  email_enabled?: boolean;
  push_enabled?: boolean;
  digest_enabled?: boolean;
}): Promise<void> {
  await invokeWrite({ action: "update_preferences", payload });
}

/**
 * Admin-only: create an in-app system notification for a defined audience.
 *
 * Routed through `notifications-write` (action `admin_create_notification`).
 * The frontend MUST NOT touch `notifications` / `notification_user_inbox`
 * directly — the edge function enforces admin role, validates input, and
 * resolves recipient IDs server-side.
 */
export async function adminCreateNotification(
  payload: AdminCreateNotificationPayload,
): Promise<AdminCreateNotificationResult> {
  const result = await invokeWrite<{
    notification_id?: string;
    recipient_count?: number;
  }>({
    action: "admin_create_notification",
    payload,
  });
  return {
    notification_id: String(result?.notification_id ?? ""),
    recipient_count: Number(result?.recipient_count ?? 0),
  };
}

/**
 * Admin-only: schedule an in-app system notification for delayed delivery.
 *
 * Routed through `notifications-write` (action `admin_schedule_notification`).
 * Recipients are resolved on the worker at send time, not now — `admins`
 * and `all_users` therefore reflect the system state when the job runs.
 */
export async function adminScheduleNotification(
  payload: AdminScheduleNotificationPayload,
): Promise<AdminScheduleNotificationResult> {
  const result = await invokeWrite<{
    ok?: boolean;
    job_id?: string | null;
    scheduled_for?: string;
  }>({
    action: "admin_schedule_notification",
    payload,
  });
  return {
    ok: Boolean(result?.ok ?? true),
    job_id: result?.job_id ?? null,
    scheduled_for: String(result?.scheduled_for ?? payload.scheduled_for),
  };
}

/**
 * Admin-only: cancel a still-pending scheduled notification job.
 *
 * Routed through `notifications-write`
 * (action `admin_cancel_scheduled_notification`). The frontend MUST NOT
 * touch `job_queue` directly — the edge function enforces admin role,
 * verifies the job type, and only allows transitions from `pending` →
 * `cancelled`.
 */
export async function adminCancelScheduledNotification(
  jobId: string,
): Promise<{ ok: boolean; job_id: string }> {
  const result = await invokeWrite<{ ok?: boolean; job_id?: string }>({
    action: "admin_cancel_scheduled_notification",
    payload: { job_id: jobId },
  });
  return {
    ok: Boolean(result?.ok ?? true),
    job_id: String(result?.job_id ?? jobId),
  };
}

/* ── Templates: read + write via notifications-write ─────────────────── */

export async function adminListNotificationTemplates(
  includeInactive = false,
): Promise<NotificationTemplate[]> {
  const result = await invokeWrite<{ templates?: unknown[] }>({
    action: "admin_list_notification_templates",
    payload: { include_inactive: includeInactive },
  });
  return (result?.templates ?? []) as NotificationTemplate[];
}

export async function adminUpsertNotificationTemplate(
  payload: AdminUpsertNotificationTemplatePayload,
): Promise<NotificationTemplate> {
  const result = await invokeWrite<{ template?: NotificationTemplate }>({
    action: "admin_upsert_notification_template",
    payload,
  });
  if (!result?.template) {
    throw new Error("Upsert returned no template");
  }
  return result.template;
}

export async function adminArchiveNotificationTemplate(
  id: string,
): Promise<void> {
  await invokeWrite({
    action: "admin_archive_notification_template",
    payload: { id },
  });
}

/* ── Email Provider Test ─────────────────────────────────────────────── */

export interface AdminRunEmailTestResult {
  ok: boolean;
  notification_id: string;
  delivery_id: string | null;
  delivery_channel: string | null;
  delivery_records_created: number;
  delivery_jobs_enqueued: number;
  skipped_reason: string | null;
  correlation_id: string;
  provider_enabled_hint: boolean;
}

/**
 * Admin-only: trigger an atomic, end-to-end email pipeline self-test.
 * Always targets the actor (audience = self), always uses the
 * narrow-only `system_email_test` type. Returns identifiers the UI
 * uses to poll the resulting delivery + audit trail.
 */
export async function adminRunEmailTest(
  payload: { title?: string; body?: string } = {},
): Promise<AdminRunEmailTestResult> {
  const result = await invokeWrite<Partial<AdminRunEmailTestResult>>({
    action: "admin_run_email_test",
    payload,
  });
  return {
    ok: Boolean(result?.ok ?? true),
    notification_id: String(result?.notification_id ?? ""),
    delivery_id: result?.delivery_id ?? null,
    delivery_channel: result?.delivery_channel ?? null,
    delivery_records_created: Number(result?.delivery_records_created ?? 0),
    delivery_jobs_enqueued: Number(result?.delivery_jobs_enqueued ?? 0),
    skipped_reason: result?.skipped_reason ?? null,
    correlation_id: String(result?.correlation_id ?? ""),
    provider_enabled_hint: Boolean(result?.provider_enabled_hint),
  };
}

export interface AdminRetryDeliveryResult {
  ok: boolean;
  delivery_id: string;
  notification_id: string;
  channel: string;
  retry_token: string;
  correlation_id: string;
  /**
   * Whether the backend was able to synchronously trigger a worker tick
   * after enqueuing the new job. When `true` the new attempt has already
   * been picked up (or will be within a few hundred ms) and the UI can
   * refetch the delivery row aggressively to surface the updated status.
   */
  worker_triggered: boolean;
  /** HTTP status returned by the worker-tick invocation (debug). */
  worker_status: number | null;
  /** Number of jobs the triggered worker tick processed. */
  worker_processed: number;
  /** Set when the synchronous trigger failed; the cron tick will pick up the job. */
  worker_error: string | null;
}

/**
 * Admin-only: re-enqueue a `failed` or `skipped` external delivery so the
 * email/push/digest worker tries again. The backend assigns a fresh
 * idempotency salt so the email provider does not dedupe against the
 * previous attempt.
 */
export async function adminRetryDelivery(
  deliveryId: string,
): Promise<AdminRetryDeliveryResult> {
  const result = await invokeWrite<Partial<AdminRetryDeliveryResult>>({
    action: "admin_retry_delivery",
    payload: { delivery_id: deliveryId },
  });
  return {
    ok: Boolean(result?.ok ?? true),
    delivery_id: String(result?.delivery_id ?? deliveryId),
    notification_id: String(result?.notification_id ?? ""),
    channel: String(result?.channel ?? ""),
    retry_token: String(result?.retry_token ?? ""),
    correlation_id: String(result?.correlation_id ?? ""),
    worker_triggered: Boolean(result?.worker_triggered),
    worker_status:
      typeof result?.worker_status === "number" ? result.worker_status : null,
    worker_processed:
      typeof result?.worker_processed === "number" ? result.worker_processed : 0,
    worker_error:
      typeof result?.worker_error === "string" ? result.worker_error : null,
  };
}

export interface AdminCancelDeliveryResult {
  ok: boolean;
  delivery_id: string;
  notification_id: string;
  channel: string;
  already_cancelled?: boolean;
}

/**
 * Admin-only: take a non-`sent` external delivery off the worker pipeline.
 * Cancels any in-flight job for the same delivery id and marks the
 * delivery row as `cancelled`. Sent deliveries cannot be cancelled.
 */
export async function adminCancelDelivery(
  deliveryId: string,
  reason?: string,
): Promise<AdminCancelDeliveryResult> {
  const result = await invokeWrite<Partial<AdminCancelDeliveryResult>>({
    action: "admin_cancel_delivery",
    payload: reason ? { delivery_id: deliveryId, reason } : { delivery_id: deliveryId },
  });
  return {
    ok: Boolean(result?.ok ?? true),
    delivery_id: String(result?.delivery_id ?? deliveryId),
    notification_id: String(result?.notification_id ?? ""),
    channel: String(result?.channel ?? ""),
    already_cancelled: Boolean(result?.already_cancelled),
  };
}

// ─── Admin: email-test telemetry reads ───────────────────────────

export interface AdminEmailTestDeliveryRow {
  id: string;
  status: string;
  channel: string;
  provider: string | null;
  provider_message_id: string | null;
  attempts: number;
  last_error: string | null;
  sent_at: string | null;
  skipped_at: string | null;
  failed_at: string | null;
  metadata_json: Record<string, unknown> | null;
  updated_at: string;
}

export interface AdminEmailTestAuditRow {
  id: string;
  action: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

export async function fetchAdminEmailTestDelivery(
  deliveryId: string,
): Promise<AdminEmailTestDeliveryRow | null> {
  const { data, error } = await supabase
    .from("notification_deliveries")
    .select(
      "id, status, channel, provider, provider_message_id, attempts, last_error, sent_at, skipped_at, failed_at, metadata_json, updated_at",
    )
    .eq("id", deliveryId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as AdminEmailTestDeliveryRow | null;
}

export async function fetchAdminEmailTestAuditWindow(
  windowMinutes = 10,
): Promise<AdminEmailTestAuditRow[]> {
  const since = new Date(Date.now() - windowMinutes * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("audit_logs")
    .select("id, action, created_at, metadata")
    .like("action", "notification.%")
    .gte("created_at", since)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as AdminEmailTestAuditRow[];
}

/**
 * Confirm an email-unsubscribe token via the public edge function.
 * Centralized so UI files don't need to invoke supabase.functions directly.
 */
export async function confirmEmailUnsubscribe(token: string): Promise<void> {
  const { error } = await supabase.functions.invoke("handle-email-unsubscribe", {
    body: { token },
  });
  if (error) throw error;
}